import React from "react";
import { AlertCircle, RefreshCw, ShieldCheck } from "lucide-react";

import { K2DigioRecord, K2DigioStatus } from "../../remittance-beta/types/digio/K2Digio";
import { DigioStatusMessage } from "../../remittance-beta/utils/digioConstants";
import { InlineSpinner } from "../../remittance-beta/components/modal/DigioEsignModal/InlineSpinner";
import { ESIGN_CARD_CLASS, esignStyles as styles } from "../styles";

interface EsignCardProps {
  record: K2DigioRecord;
  status: K2DigioStatus;
  statusCopy: DigioStatusMessage;
  statusIcon: React.ReactNode;
  statusIconBg: string;
  steps: string[];
  cancelledByUser: boolean;
  cancelledMessage: string;
  error: string | null;
  completed: boolean;
  showSignActions: boolean;
  showRefreshAction: boolean;
  isActionLoading: boolean;
  signing: boolean;
  onStartSigning: () => void;
  onRefreshAndSign: () => void;
  onRefreshStatus: () => void;
}

const EsignCard: React.FC<EsignCardProps> = ({
  record,
  status,
  statusCopy,
  statusIcon,
  statusIconBg,
  steps,
  cancelledByUser,
  cancelledMessage,
  error,
  completed,
  showSignActions,
  showRefreshAction,
  isActionLoading,
  signing,
  onStartSigning,
  onRefreshAndSign,
  onRefreshStatus,
}) => {
  const showSummary = Boolean(record.docName || record.signerName || record.transactionPurpose);
  const showStatusDescription = Boolean(statusCopy.description) && status !== "AWAITING_SIGN";

  return (
    <div className={ESIGN_CARD_CLASS}>
      <div className={styles.cardBody}>
        <div className={styles.hero}>
          {completed ? (
            <h1 className={styles.cardTitle}>A2 Form Signed</h1>
          ) : (
            <>
              {/* <p className={styles.eyebrow}>A2 E-SIGN</p> */}
              <h1 className={styles.cardTitle}>A2 Form e-Sign</h1>
              <p className={styles.cardSubtitle}>
                Complete Aadhaar-based e-sign to submit your A2 form securely.
              </p>
            </>
          )}
        </div>

        {showSummary ? (
          <div className={styles.summaryHeader}>
            <div>
              <p className={styles.summaryHeaderLabel}>Document</p>
              <p className={styles.summaryHeaderValue}>{record.docName || "—"}</p>
            </div>
            <div>
              <p className={styles.summaryHeaderLabel}>Signer</p>
              <p className={styles.summaryHeaderValue}>{record.signerName || "—"}</p>
            </div>
            <div>
              <p className={styles.summaryHeaderLabel}>Purpose</p>
              <p className={styles.summaryHeaderValue}>{record.transactionPurpose || "—"}</p>
            </div>
          </div>
        ) : null}

        <div className={styles.statusRow}>
          <div className={`${styles.statusIconWrap} ${statusIconBg}`}>{statusIcon}</div>
          <div className="min-w-0 flex-1">
            <h2 className={styles.statusTitle}>{statusCopy.title}</h2>
            {showStatusDescription ? (
              <p className={styles.statusDescription}>{statusCopy.description}</p>
            ) : null}
          </div>
        </div>

        {showSignActions ? (
          <div className={styles.stepsWrap}>
            {steps.map((step, index) => (
              <div key={step} className={styles.stepRow}>
                <span className={styles.stepNumber}>{index + 1}</span>
                <p className={styles.stepText}>{step}</p>
              </div>
            ))}
          </div>
        ) : null}

        {cancelledByUser ? (
          <div className={`${styles.alert} ${styles.alertWarning}`}>{cancelledMessage}</div>
        ) : null}

        {error ? <div className={`${styles.alert} ${styles.alertError}`}>{error}</div> : null}

        {completed ? (
          <div className={`${styles.alert} ${styles.alertSuccess}`}>
            Your e-sign is complete. You can close this page and return to the review screen.
          </div>
        ) : null}

        {status === "DO_LATER" ? (
          <div className={`${styles.alert} ${styles.alertWarning}`}>
            Please return to the review screen to resume your A2 form e-sign.
          </div>
        ) : null}

        {status === "LINK_EXPIRED" ? (
          <div className={`${styles.alert} ${styles.alertError}`}>
            Request a new A2 form signing link from the review screen to continue.
          </div>
        ) : null}

        {signing && showSignActions ? (
          <p className={styles.hintText}>
            Complete the Aadhaar e-sign in the Digio window. Do not close this page.
          </p>
        ) : null}

        {(showSignActions || showRefreshAction) && (
          <div className={styles.actions}>
            {showSignActions ? (
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={onStartSigning}
                disabled={isActionLoading}
              >
                {signing ? (
                  <>
                    <InlineSpinner className="text-white" />
                    Opening Digio...
                  </>
                ) : (
                  <>
                    <ShieldCheck className="h-[18px] w-[18px]" />
                    {cancelledByUser ? "Resume Aadhaar e-Sign" : "Start Aadhaar e-Sign"}
                  </>
                )}
              </button>
            ) : null}

            {showRefreshAction ? (
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={onRefreshAndSign}
                disabled={isActionLoading}
              >
                {isActionLoading ? (
                  <>
                    <InlineSpinner className="text-white" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-[18px] w-[18px]" />
                    Refresh &amp; Continue
                  </>
                )}
              </button>
            ) : null}

            {!showRefreshAction && showSignActions ? (
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={onRefreshStatus}
                disabled={isActionLoading}
              >
                <RefreshCw className="h-[18px] w-[18px]" />
                Refresh status
              </button>
            ) : null}
          </div>
        )}

        <p className={styles.footerNote}>Secure Aadhaar-based e-sign powered by Digio</p>
      </div>
    </div>
  );
};

interface EsignErrorCardProps {
  error: string;
  onRetry: () => void;
}

export const EsignErrorCard: React.FC<EsignErrorCardProps> = ({ error, onRetry }) => (
  <div className={ESIGN_CARD_CLASS}>
    <div className={styles.cardBody}>
      <div className={styles.hero}>
        {/* <p className={styles.eyebrow}>A2 E-SIGN</p> */}
        <h1 className={styles.cardTitle}>A2 Form e-Sign</h1>
      </div>
      <div className={`${styles.statusIconWrap} bg-red-50`}>
        <AlertCircle className="h-5 w-5 text-red-500" />
      </div>
      <h2 className={styles.statusTitle}>Unable to load e-sign</h2>
      <div className={`${styles.alert} ${styles.alertError}`}>{error}</div>
      <button type="button" className={styles.primaryBtn} onClick={onRetry}>
        <RefreshCw className="h-[18px] w-[18px]" />
        Try again
      </button>
      <p className={styles.footerNote}>Secure Aadhaar-based e-sign powered by Digio</p>
    </div>
  </div>
);

export default EsignCard;
